<template>
  <div class="chart-container mx-auto">
    <line-chart
      class="measurement-chart"
      v-if="initialized"
      :options="chartOptions"
      :chart-data="datacollection"
    ></line-chart>

    <div class="chart-scale-selection d-flex justify-content-center mx-auto mt-3 mr-1 ml-1">
      <button
        v-for="scalingOption in scalingOptions"
        v-bind:key="scalingOption"
        class="btn btn-sm btn-outline-primary ml-2 mr-2"
        v-bind:class="{ active: scalingOption == chartScaleSelection }"
        type="submit"
        v-on:click="setChartScale(scalingOption)"
      >{{ scalingOption }}</button>
    </div>

    <div
      class="chart-filter d-flex justify-content-center mx-auto mt-3"
      v-if="chartScaleSelection == 'Custom'"
    >
      <input type="date" v-model="minDate" class="form-control ml-2 mr-2" placeholder="Start date" />
      <input type="date" v-model="maxDate" class="form-control ml-2 mr-2" placeholder="End date" />
      <button
        type="button"
        class="btn btn-outline-primary ml-2 mr-2"
        v-on:click="applyCustomFilter"
      >Apply</button>
    </div>
  </div>
</template>

<script>
import { store } from "@/store.js";
import LineChart from "../components/LineChart.js";

export default {
  name: "MeasurementChart",
  components: { LineChart },
  props: {
    measurements: Array,
    measurementName: String,
    measurementUnit: String,
    measurementDivider: Number,
    measurementProperty: String,
    color: {
      type: String,
      default: "rgb(201, 203, 207)"
    }
  },
  data: function() {
    return {
      store: store,
      initialized: false,
      filteredMeasurements: [],
      chartData: [],
      minDate: this.moment(this.moment().year() + "01-01", "YYYY-MM-DD").format(
        "YYYY-MM-DD"
      ),
      maxDate: this.moment().format("YYYY-MM-DD"),
      // chartColors: {
      //   red: "rgb(255, 99, 132)",
      //   orange: "rgb(255, 159, 64)",
      //   yellow: "rgb(255, 205, 86)",
      //   green: "rgb(75, 192, 192)",
      //   blue: "rgb(54, 162, 235)",
      //   purple: "rgb(153, 102, 255)",
      //   grey: "rgb(201, 203, 207)"
      // },
      scalingOptions: ["Month", "3 months", "Year", "Custom"],
      chartScaleSelection: "Month",
      datacollection: {
        datasets: [
          {
            label: this.measurementName,
            fill: false,
            borderColor: this.color,
            backgroundColor: this.color,
            pointRadius: 2,
            data: this.chartDataComputed //[]
          }
        ]
      },
      chartOptions: {
        responsive: true,
        maintainAspectRatio: false,
        elements: {
          line: {
            tension: 0
          }
        },
        title: {
          display: true,
          text: `${this.measurementName} [${this.measurementUnit}]`
        },
        legend: {
          display: false
        },
        scales: {
          xAxes: [
            {
              type: "time",
              time: {
                min: "2019-01-01",
                max: "2019-12-31",
                unit: "month",
                displayFormats: {
                  day: "DD.MM.YYYY",
                  week: "DD.MM.YYYY",
                  month: "MM/YYYY"
                }
              }
            }
          ],
          yAxes: [
            {
              type: "linear",
              ticks: {
                beginAtZero: false,
                suggestedMin: 0,
                suggestedMax: 150,
                stepSize: 5,
              },
              display: true,
              scaleLabel: {
                display: false,
                labelString:
                  this.measurementName + " [" + this.measurementUnit + "]"
              }
            }
          ]
        }
      }
    };
  },
  created: function() {},
  mounted: function() {
    this.setChartScale("Month");
    this.updateChart();
  },
  computed: {
    chartDataComputed: function() {
      return this.chartData;
    }
  },
  methods: {
    filterMeasurements() {
      let self = this;
      return this.measurements.filter(function(m) {
        let measurementDateTime = self.store.getMeasurementDatetime(m);

        let min = self.minDate
          ? self.moment(self.minDate, "YYYY-MM-DD")
          : undefined;
        let max = self.maxDate
          ? self.moment(self.maxDate + " 23:59:59", "YYYY-MM-DD HH:mm:ss")
          : undefined;

        if (min && max)
          return measurementDateTime >= min && measurementDateTime <= max;

        if (min) return measurementDateTime >= min;

        if (max) return measurementDateTime <= max;

        return true;
      });
    },
    setChartScale(selection) {
      let max = this.moment().add(1, "days");
      let min = this.moment();

      this.chartScaleSelection = selection;

      if (selection == "Month") {
        this.chartOptions.scales.xAxes[0].time.unit = "week";
        this.updateTimeScale(min.subtract(1, "months"), max);
      } else if (selection == "3 months") {
        this.chartOptions.scales.xAxes[0].time.unit = "week";
        this.updateTimeScale(min.subtract(3, "months"), max);
      } else if (selection == "Year") {
        this.chartOptions.scales.xAxes[0].time.unit = "month";
        this.updateTimeScale(min.subtract(12, "months"), max);
      } else {
        this.chartScaleSelection = "Custom";
      }
    },
    updateTimeScale(minDateMoment, maxDateMoment) {
      this.minDate = minDateMoment.format("YYYY-MM-DD");
      this.maxDate = maxDateMoment.format("YYYY-MM-DD");

      console.log("Min date=" + this.minDate + ", max date=" + this.maxDate);

      this.updateChart();
    },
    applyCustomFilter() {
      this.chartOptions.scales.xAxes[0].time.unit = "";
      this.updateChart();
    },
    updateChart() {
      let self = this;
      this.filteredMeasurements = this.filterMeasurements();

      this.filteredMeasurements.sort(this.store.compareByDatetimeOldestFirst);

      this.chartData = [];

      let min = Math.min.apply(
        Math,
        this.filteredMeasurements.map(function(m) {
          return m[self.measurementProperty] / self.measurementDivider;
        })
      );

      let max = Math.max.apply(
        Math,
        this.filteredMeasurements.map(function(m) {
          return m[self.measurementProperty] / self.measurementDivider;
        })
      );

      let chartMax = max + (5 - (max % 5));
      let chartMin = min - (min % 5);

      if (chartMax == max)
        chartMax = chartMax + 5;

      if (chartMin == min)
        chartMin = chartMin - 5;

      if (chartMin < 0)
        chartMin = 0;

      this.chartOptions.scales.yAxes[0].ticks.suggestedMax = chartMax;
      this.chartOptions.scales.yAxes[0].ticks.suggestedMin = chartMin;

      this.chartOptions.scales.xAxes[0].time.min = this.minDate || "";
      this.chartOptions.scales.xAxes[0].time.max = this.maxDate || "";

      for (let i = 0; i < this.filteredMeasurements.length; i++) {
        let measurement = this.filteredMeasurements[i];

        let x = this.store.getMeasurementDatetime(measurement);

        if (x) {
          this.chartData.push({
            x: x,
            y: measurement[this.measurementProperty] / this.measurementDivider
          });
        }
      }

      this.datacollection.datasets[0].label = this.measurementName;
      this.datacollection.datasets[0].data = this.chartData;

      this.initialized = true;
    }
  }
};
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped>
.chart-container {
  margin-top: 1rem;
  max-height: 500px;
  /* max-width: 800px; */
}

.measurement-chart {
  margin-top: 2.5rem;
  /* position: relative; */
}

.chart-scale-selection {
  max-width: 500px;
}

.chart-filter {
  /* margin-top: 1em; */
  max-width: 500px;
}
</style>
